import * as R from 'ramda';
import * as Promise from 'bluebird';
import { of, from, Observable } from 'rxjs';
import { mergeMap, map, catchError } from 'rxjs/operators';
import { combineEpics, ofType } from 'redux-observable';
import { types as loginFormTypes } from 'components/loginForm/loginForm.redux';
import {
  syncDeviceError,
  syncLocalDatabaseChecked,
  syncLocalImagesSynced,
  syncLocalDataSynced,
  syncDeviceLocalStart,
  syncDeviceLocalProgress,
  syncDeviceLocalEnd,
  syncDeviceRemoteStart,
  syncDeviceRemoteProgress,
  syncDeviceRemoteEnd,
  types,
} from './syncDeviceModal.redux';
import TripleDES from 'crypto-js/tripledes';
import { api } from 'components/api';
import { fileSystem, sendData } from 'components/fileSystem';
import { now, encryptSession, getDatabaseName } from '../../utils';
import { openSQLiteDatabase, executeSql } from '../db/database';
import { getAppConfig } from '../../appConfig';
import { datasources, initDatasources } from '../db/datasources';
import async from 'async-es';

const setupLocalSession = ({ userData, accessData, loginData }) => {
  const userSession = {
    username: userData.username,
    rememberMe: loginData.rememberMe,
    loginSince: now(),
    access_token: accessData.access_token,
    user_data: userData,
    user_database: `spectre_dbuser_${userData.id.replace(/-/g, '')}`,
    last_user: {
      last_user: userData.username,
      last_password: TripleDES.encrypt(loginData.password, userData.id).toString(),
      last_login: now(),
      last_proshop: userData.proshops[0].proshop.id,
      api_token: accessData.access_token,
      refresh_token: accessData.refresh_token,
      expires_in: accessData.expires_in,
      last_token_refresh: now(),
    },
    currentProshop: userData.proshops[0].proshop.id,
    checkOfflineOn: true,
  };

  encryptSession(userSession, userData.id);

  return userSession;
};

const syncCheckLocalDatabaseEpic = action$ =>
  action$.pipe(
    ofType(loginFormTypes.handleLoginFormLoginResponse),
    mergeMap(({ payload }) => {
      const { userData, deviceData, needsSync } = payload;
      const sqliteUUID = `lower(hex(randomblob(4))) || '-' || lower(hex(randomblob(2))) || '-4' || substr(lower(hex(randomblob(2))),2) || '-' || substr('89ab',abs(random()) % 4 + 1, 1) || substr(lower(hex(randomblob(2))),2) || '-' || lower(hex(randomblob(6)))`;
      const device_local_user_session = JSON.parse(deviceData.device_local_user_session);

      console.log(payload);

      if (userData.id !== deviceData.id) {
        // need to clone and rename database
        const { renameDatabase } = fileSystem();
        const remoteDatabaseName = `spectre_dbuser_${userData.id.replace(/-/g, '')}`;
        const setRemoteUserAndProshop = () =>
          new Promise((success, fail) => {
            openSQLiteDatabase({
              alias: 'spectre_db_remote',
              databaseName: remoteDatabaseName,
              databaseVersion: getAppConfig().databaseVersion,
              databaseDescription: 'remoteSpectreDB',
            }).then(() => {
              console.log('DATABASE OPEN!!!!!');

              const remoteDBExecuteSql = executeSql('spectre_db_remote');
              const proshopId = userData.proshops[0].proshop.id;

              const updateQueue = [];
              const deleteQueue = [];
              const insertQueue = [];

              updateQueue.push({ sql: 'UPDATE app_user SET id=?', params: [userData.id] });
              // updateQueue.push({ sql: 'UPDATE app_user_audit SET id=?', params: [userData.id] });
              updateQueue.push({ sql: 'UPDATE proshop SET id=?', params: [proshopId] });
              // updateQueue.push({ sql: 'UPDATE proshop_audit SET id=?', params: [proshopId] });
              updateQueue.push({
                sql: 'UPDATE app_user_proshop SET user_id=?, proshop_id=?',
                params: [userData.id, proshopId],
              });
              // updateQueue.push({
              //   sql: 'UPDATE app_user_proshop_audit SET user_id=?, proshop_id=?',
              //   params: [userData.id, proshopId],
              // });
              updateQueue.push({
                sql: 'UPDATE customer_proshop SET proshop_id=?',
                params: [proshopId],
              });
              // updateQueue.push({
              //   sql: 'UPDATE customer_proshop_audit SET proshop_id=?',
              //   params: [proshopId],
              // });

              deleteQueue.push({
                sql: 'DELETE FROM customer_audit',
                params: [],
              });
              insertQueue.push({
                sql: `INSERT INTO customer_audit(audit_id, id, updated_by, created_by, created_at, updated_at, firstname, lastname, email, password, telephone, address, country, state, city, postal_code, preferred_lang, photo, pap_h, pap_v, pap_ud, axis_rot, axis_tilt, ball_speed, rev_rate, hand, style, signature, revtype) 
                    SELECT id as audit_id, id, updated_by, created_by, created_at, updated_at, firstname, lastname, email, password, telephone, address, country, state, city, postal_code, preferred_lang, photo, pap_h, pap_v, pap_ud, axis_rot, axis_tilt, ball_speed, rev_rate, hand, style, signature, 'INS' as revtype FROM customer`,
                params: [],
              });
              deleteQueue.push({
                sql: 'DELETE FROM customer_proshop',
                params: [],
              });
              insertQueue.push({
                sql: `INSERT INTO customer_proshop(id, updated_by, created_by, proshop_id, customer_id, created_at, updated_at) 
                    SELECT (${sqliteUUID}) as id, updated_by, created_by, '${proshopId}' as proshop_id, id as customer_id, created_at, updated_at FROM customer`,
                params: [],
              });
              deleteQueue.push({
                sql: 'DELETE FROM customer_proshop_audit',
                params: [],
              });
              insertQueue.push({
                sql: `INSERT INTO customer_proshop_audit(audit_id, id, updated_by, created_by, proshop_id, customer_id, created_at, updated_at, revtype) 
                    SELECT id as audit_id, id, updated_by, created_by, proshop_id, customer_id, created_at, updated_at, 'INS' as revtype FROM customer_proshop`,
                params: [],
              });
              deleteQueue.push({
                sql: 'DELETE FROM spec_sheet_audit',
                params: [],
              });
              insertQueue.push({
                sql: `INSERT INTO spec_sheet_audit(audit_id, id, updated_by, created_by, customer_id, created_at, updated_at, f1_rev, f1_lat, f1_for, f1_grip_type, f1_od, f1_size, bridge, f2_rev, f2_lat, f2_for, f2_grip_type, f2_od, f2_size, f1_span_full, f1_span_cut, f1_span_outer2_cut, f1_span_insert2_cut, f2_span_full, f2_span_cut, f2_span_outer2_cut, f2_span_insert2_cut, thm_rev, thm_lat_l, thm_lat_r, thm_for, thm_size, thm_insert_type, thm_flex, note, hand, ball_grip, thm_type, thm_od, thm_oval_taper, thm_round_taper, thm_oval_width, thm_oval_degree, clt, name, f1_oval, f2_oval, revtype) 
                    SELECT id as audit_id, id, updated_by, created_by, customer_id, created_at, updated_at, f1_rev, f1_lat, f1_for, f1_grip_type, f1_od, f1_size, bridge, f2_rev, f2_lat, f2_for, f2_grip_type, f2_od, f2_size, f1_span_full, f1_span_cut, f1_span_outer2_cut, f1_span_insert2_cut, f2_span_full, f2_span_cut, f2_span_outer2_cut, f2_span_insert2_cut, thm_rev, thm_lat_l, thm_lat_r, thm_for, thm_size, thm_insert_type, thm_flex, note, hand, ball_grip, thm_type, thm_od, thm_oval_taper, thm_round_taper, thm_oval_width, thm_oval_degree, clt, name, f1_oval, f2_oval, 'INS' as revtype FROM spec_sheet`,
                params: [],
              });
              deleteQueue.push({
                sql: 'DELETE FROM thumb_cut_audit',
                params: [],
              });
              insertQueue.push({
                sql: `INSERT INTO thumb_cut_audit(audit_id, id, updated_by, created_by, spec_sheet_id, created_at, updated_at, v, h, revtype) 
                    SELECT id as audit_id, id, updated_by, created_by, spec_sheet_id, created_at, updated_at, v, h, 'INS' as revtype FROM thumb_cut`,
                params: [],
              });
              deleteQueue.push({
                sql: 'DELETE FROM ball_drilled_audit',
                params: [],
              });
              insertQueue.push({
                sql: `INSERT INTO ball_drilled_audit(audit_id, id, updated_by, created_by, customer_id, spec_sheet_id, created_at, updated_at, manufacturer, ball_name, layout, surface_preparation, weight_hole_position, weight_hole_size, weight_hole_depth, weight, photo, revtype) 
                    SELECT id as audit_id, id, updated_by, created_by, customer_id, spec_sheet_id, created_at, updated_at, manufacturer, ball_name, layout, surface_preparation, weight_hole_position, weight_hole_size, weight_hole_depth, weight, photo, 'INS' as revtype FROM ball_drilled`,
                params: [],
              });

              remoteDBExecuteSql({ queue: updateQueue })
                .then(() => console.log('update local db done!') || remoteDBExecuteSql({ queue: deleteQueue }))
                .then(() => console.log('delete audit records done!') || remoteDBExecuteSql({ queue: insertQueue }))
                .then(() => {
                  console.log('insert new audit records done!');
                  success();
                })
                .catch(error => {
                  console.log('error updating previous database', error);
                  success();
                });
            });
          });

        const setLocalDatabase = R.pipeP(
          () =>
            renameDatabase(
              getDatabaseName(device_local_user_session.user_database, getAppConfig().databaseVersion),
              getDatabaseName(remoteDatabaseName, getAppConfig().databaseVersion)
            ),
          setRemoteUserAndProshop,
          () => Promise.resolve(setupLocalSession(payload))
        );

        return from(setLocalDatabase()).pipe(
          map(() => {
            if (!needsSync) {
              setupLocalSession(payload);
              return syncLocalImagesSynced(payload);
            } else {
              setupLocalSession(payload);
              return syncLocalDatabaseChecked(payload);
            }
          }),
          catchError(error => {
            console.warn(error);
            return of(syncDeviceError({ error }));
          })
        );
      } else {
        if (!needsSync) {
          setupLocalSession(payload);
          return of(syncLocalImagesSynced(payload));
        } else {
          const remoteDatabaseName = `spectre_dbuser_${userData.id.replace(/-/g, '')}`;

          return from(
            openSQLiteDatabase({
              alias: 'spectre_db_remote',
              databaseName: remoteDatabaseName,
              databaseVersion: getAppConfig().databaseVersion,
              databaseDescription: 'remoteSpectreDB',
            })
          ).pipe(
            map(() => {
              console.log('DATABASE OPEN!!!!!');

              setupLocalSession(payload);
              return syncLocalDatabaseChecked(payload);
            })
          );
        }
      }
    })
  );

const syncLocalDataDatabaseEpic = action$ =>
  action$.pipe(
    ofType(types.syncLocalDatabaseChecked),
    mergeMap(({ payload }) =>
      Observable.create(syncActionsObserver => {
        const { userData, deviceData } = payload;
        const { setUserSessionForceLocalSync } = api;
        const syncActions = {
          localSyncStart: () => syncActionsObserver.next(syncDeviceLocalStart()),
          localSyncProgress: progress => syncActionsObserver.next(syncDeviceLocalProgress({ progress })),
          localSyncEnd: () => syncActionsObserver.next(syncDeviceLocalEnd()),
          remoteSyncStart: () => syncActionsObserver.next(syncDeviceRemoteStart()),
          remoteSyncProgress: progress => syncActionsObserver.next(syncDeviceRemoteProgress({ progress })),
          remoteSyncEnd: () => syncActionsObserver.next(syncDeviceRemoteEnd()),
        };

        if (userData.id !== deviceData.id) {
          // need to clone and rename database
          const syncDatabase = () =>
            new Promise((success, fail) => {
              initDatasources('spectre_db_remote', userData.id, syncActions)
                .then(() => {
                  console.info('Datasources initalized.');
                  setUserSessionForceLocalSync(true);

                  return R.pipeP(
                    datasources.customerDS.sync,
                    datasources.customerProshopDS.sync,
                    datasources.specSheetDS.sync,
                    datasources.thumbCutDS.sync,
                    // datasources.thumbInfoDS.sync,
                    datasources.ballDrilledDS.sync
                  )();
                })
                .then(() => {
                  setUserSessionForceLocalSync(false);
                  success();
                })
                .catch(fail);
            });

          syncDatabase()
            .then(() => {
              syncActionsObserver.next(syncLocalImagesSynced(payload));
            })
            .catch(error => {
              console.warn(error);
              syncActionsObserver.next(syncDeviceError({ error }));
            });
        } else {
          syncActionsObserver.next(syncLocalImagesSynced(payload));
        }
      })
    )
  );

const syncLocalImagesEpic = action$ =>
  action$.pipe(
    ofType(types.syncLocalDataSynced),
    mergeMap(({ payload }) =>
      Observable.create(syncActionsObserver => {
        const { getImageDirectory, zipSyncImage, removeSyncZipFile } = fileSystem();
        const { getSessionApiUrl } = api;

        const getImages = specsheetId =>
          new Promise((success, reject) => {
            getImageDirectory()
              .then(imageDirectoryReader => {
                imageDirectoryReader.createReader().readEntries(directoryEntries =>
                  R.pipe(
                    R.filter(file => file.name.indexOf(specsheetId) >= 0),
                    success
                  )(directoryEntries)
                );
              })
              .catch(reject);
          });

        const imageDBExecuteSql = executeSql('spectre_db_remote');

        const customerSql = `SELECT * FROM customer`;
        const specSheetSql = `SELECT * FROM spec_sheet`;
        const ballDrilledSql = `SELECT * FROM ball_drilled`;

        const getCustomersImagesPath = new Promise(success =>
          imageDBExecuteSql({ sql: customerSql, params: [] })
            .then(customers =>
              R.pipe(
                R.map(customer =>
                  customer.photo && customer.photo !== '' && customer.photo !== 'null'
                    ? R.pipe(
                        R.split('/'),
                        R.last
                      )(customer.photo)
                    : ''
                ),
                R.without(['']),
                success
              )(customers)
            )
            .catch(error => console.log('error', error) || success([]))
        );
        const getBallDrilledImagesPath = new Promise(success =>
          imageDBExecuteSql({ sql: ballDrilledSql, params: [] })
            .then(ballsDrilled =>
              R.pipe(
                R.map(ballDrilled =>
                  ballDrilled.photo && ballDrilled.photo !== '' && ballDrilled.photo !== 'null'
                    ? R.pipe(
                        R.split('/'),
                        R.last
                      )(ballDrilled.photo)
                    : ''
                ),
                R.without(['']),
                success
              )(ballsDrilled)
            )
            .catch(error => console.log('error', error) || success([]))
        );
        const getSpecSheetImagesPath = new Promise(success =>
          imageDBExecuteSql({ sql: specSheetSql, params: [] })
            .then(specSheets => Promise.all(specSheets.map(specSheet => getImages(specSheet.id))))
            .then(paths =>
              success(
                R.pipe(
                  R.flatten,
                  R.map(path => path.name)
                )(paths)
              )
            )
            .catch(error => console.log('error', error) || success([]))
        );

        const getImagePaths = () =>
          new Promise(success =>
            Promise.all([getCustomersImagesPath, getSpecSheetImagesPath, getBallDrilledImagesPath]).then(
              ([customersImagesPath, specSheetImagesPath, ballDrilledImagesPath]) =>
                success([...customersImagesPath, ...specSheetImagesPath, ...ballDrilledImagesPath])
            )
          );

        const generateZipFile = imagePath => console.log('images paths', imagePath) || zipSyncImage(imagePath);

        const sendImageToServer = zipImageFile =>
          console.log('zipImagesFile', zipImageFile) ||
          R.pipeP(
            () => getSessionApiUrl('images/upload'),
            url => console.log('url', url) || sendData({ file: zipImageFile, url })
          )();

        const sendImagesToServer = imagePaths =>
          new Promise(success => {
            const totData = imagePaths.length;
            let imagesSent = 0;

            syncActionsObserver.next(syncDeviceProccess({ proccess: 'sending images' }));
            async.eachOfLimit(
              imagePaths,
              500,
              (imagePath, imagePathIdx, done) => {
                const checkImagesSent = () => {
                  imagesSent = imagesSent + 1;
                  const percentage = ~~((imagesSent / totData) * 100);
                  console.log(`images processed ${percentage} %`);
                  syncActionsObserver.next(syncDevicePercentage({ percentage }));
                  done();
                };

                generateZipFile(imagePath).then(zipImageFile => {
                  console.log('image zipped', zipImageFile);
                  sendImageToServer(zipImageFile)
                    .then(() => checkImagesSent())
                    .catch(error => console.warn(error) || checkImagesSent());
                  const percentage = ~~(((imagePathIdx + 1) / totData) * 100);
                  console.log(`sending images ${percentage} %`);
                });
              },
              () => {
                console.log(`images sent`);
                success();
              }
            );
          });

        const syncImages = R.pipeP(
          getImagePaths,
          sendImagesToServer
        );

        syncImages()
          .then(() => syncActionsObserver.next(syncLocalImagesSynced(payload)))
          .catch(error => console.warn(error) || syncActionsObserver.next(syncDeviceError({ error })));

        // return from(syncImages()).pipe(
        //   map(() => {
        //     return syncLocalImagesSynced(payload);
        //   }),
        //   catchError(error => {
        //     console.warn(error);
        //     return of(syncDeviceError({ error }));
        //   })
        // );
      })
    )
  );

export default combineEpics(syncCheckLocalDatabaseEpic, syncLocalDataDatabaseEpic, syncLocalImagesEpic);
